import {
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormControl,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { fuseAnimations } from '@fuse/animations/public-api';
import { debounceTime, distinctUntilChanged, filter, Subject, takeUntil } from 'rxjs';
import { SearchService } from './search-page.service';
import { NavigationEnd, Router } from '@angular/router';

@Component({
    selector: 'search-page',
    templateUrl: './search-page.component.html',
    encapsulation: ViewEncapsulation.None,
    exportAs: 'fuseSearchPage',
    animations: fuseAnimations,
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        MatOptionModule,
        MatFormFieldModule,
        MatInputModule,
    ],
    providers: [
    ],
})
export class SearchPageComponent implements OnChanges, OnInit, OnDestroy {
    @Input() appearance: 'basic' | 'bar' = 'basic';
    @Input() debounce: number = 300;
    @Input() minLength: number = 2;
    @Output() search: EventEmitter<any> = new EventEmitter<any>();

    opened: boolean = false;
    searchControl: UntypedFormControl = new UntypedFormControl();
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    private lastCleanUrl: string = '';
    isFilterPanelOpen: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private searchService: SearchService,
        private _router: Router
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any {
        return {
            'search-appearance-bar': this.appearance === 'bar',
            'search-appearance-basic': this.appearance === 'basic',
            'search-opened': this.opened,
        };
    }

    /**
     * Setter for bar search input
     *
     * @param value
     */
    @ViewChild('barSearchInput')
    set barSearchInput(value: ElementRef) {
        // If the value exists, it means that the search input
        // is now in the DOM, and we can focus on the input..
        if (value) {
            // Give Angular time to complete the change detection cycle
            setTimeout(() => {
                // Focus to the input element
                value.nativeElement.focus();
            });
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On changes
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        // Appearance
        if ('appearance' in changes) {
            // To prevent any issues, close the
            // search after changing the appearance
            this.close();
        }
    }

    /**
     * On init
     */
    ngOnInit(): void {
        this.listenEvents();
        this.setupFilterPanelObserver();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On keydown of the search input
     *
     * @param event
     */
    onKeydown(event: KeyboardEvent): void {
        // Escape
        if (event.code === 'Escape') {
            // If the appearance is 'bar', close the search
            if (this.appearance === 'bar') {
                this.close();
            }
        }
    }

    /**
     * Open the search
     * Used in 'bar'
     */
    open(): void {
        // Return if it's already opened
        if (this.opened) {
            return;
        }

        // Check filter panel state before opening
        this.checkFilterPanelState();

        // Open the search
        this.opened = true;
    }

    /**
     * Close the search
     * * Used in 'bar'
     */
    close(): void {
        // Return if it's already closed
        if (!this.opened) {
            return;
        }

        // Clear the search input
        this.searchControl.setValue('');
        this.searchService.clear();

        // Close the search
        this.opened = false;
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    /**
     * Get the right position for the search container based on filter panel state
     */
    get searchContainerRightPosition(): string {
        // Return position based on filter panel state
        if (this.isFilterPanelOpen) {
            return 'right-[320px]'; // Move left when filter is open
        } else {
            return 'right-[90px]'; // Normal position when filter is closed
        }
    }

    /**
     * Check if the filter panel is open
     */
    private checkFilterPanelState(): void {
        const filterElement = document.querySelector('app-filter mat-sidenav') as HTMLElement;
        if (filterElement) {
            // Check if the drawer is opened by looking at the opened attribute and classes
            const isOpened = filterElement.getAttribute('ng-reflect-opened') === 'true' ||
                           filterElement.classList.contains('mat-drawer-opened') ||
                           (filterElement.style.transform && filterElement.style.transform !== 'translateX(100%)');
            
            this.isFilterPanelOpen = isOpened;
        } else {
            this.isFilterPanelOpen = false;
        }
    }

    /**
     * Setup observer to watch for filter panel changes
     */
    private setupFilterPanelObserver(): void {
        // Use MutationObserver to watch for changes in the filter panel
        const observer = new MutationObserver(() => {
            this.checkFilterPanelState();
        });

        // Start observing when the filter element becomes available
        const checkForFilter = () => {
            const filterElement = document.querySelector('app-filter mat-sidenav');
            if (filterElement) {
                observer.observe(filterElement, {
                    attributes: true,
                    attributeFilter: ['class', 'style']
                });
            } else {
                // Retry after a short delay if filter element is not found
                setTimeout(checkForFilter, 500);
            }
        };

        checkForFilter();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private listenEvents(): void {
        this.searchControl.valueChanges
        .pipe(
            debounceTime(this.debounce),
            distinctUntilChanged(),
            takeUntil(this._unsubscribeAll)
        )
        .subscribe((value: string) => {
            const search = (value || '').trim().toLowerCase();
            this.searchService.setSearchTerm(search);
        });

        this._router.events
        .pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this._unsubscribeAll)
        )
        .subscribe((event: NavigationEnd) => {
            const newCleanUrl = this.getCleanUrl(event.url);
            
            // Only clear if the base URL changed (not just query params)
            if (this.lastCleanUrl !== newCleanUrl) {
                this.clearSearch();
                this.lastCleanUrl = newCleanUrl;
            }
        });
    }

    private getCleanUrl(url: string): string {
        // Remover query parameters y fragment
        return url.split('?')[0].split('#')[0];
    }

    private clearSearch(): void {
        this.searchControl.setValue('');
        this.searchService.clear();
        
        if (this.appearance === 'bar' && this.opened) {
            this.opened = false;
        }
    }
}
